import { useEffect, useMemo, useState } from 'react';
import { Download, Receipt } from 'lucide-react';
import {
  UniversalPage,
  UniversalEmptyState,
  UniversalTable,
  UniversalFilterBar,
  PeriodFilter,
  PERIOD_LABELS,
  Card,
  Button,
  type UniversalTableColumn,
} from '@sighthost/ui';
import {
  salesService,
  type AgentCommissionRow,
  type CompanyCommissionRow,
} from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { useFilterParams, inPeriod } from '@/shared/hooks/useFilterParams';

/**
 * SalesCommissionPage — reads from the Slice 5 views.
 *
 * View `v_sales_agent_commission` shows the once-off per first-paid
 * `(target_kind, target_ref)` per customer. `v_sales_company_recurring_commission`
 * shows the company's negotiated percentage of every qualifying paid renewal
 * line — the rate is per-company (`sales_company.recurring_commission_pct`) and
 * is never shown to an agent (LD-S1).
 *
 * Both views are SECURITY INVOKER — RLS on the underlying tables scopes the
 * rows per caller. Admin sees all; manager sees own-company; agent sees own.
 *
 * Month filter: `commission_month` is a SAST first-of-month `date` (see
 * migration 20260809 — it was a UTC-bucketed timestamptz, which put a payment
 * taken between 22:00 and 23:59 UTC on a month's last day into the wrong
 * commission month). Keys come from `sastMonthKey`/`sastRecentMonths` so the
 * client and the view agree on where a month starts.
 */

type Tab = 'agent' | 'company';

function formatZAR(amount: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount ?? 0);
}

export default function SalesCommissionPage() {
  const { staffRole, isAdmin } = useStaffRole();
  // LD-S1 firewall: agents must never see the Company recurring side.
  // We hide the Company tab button + force the effective tab to 'agent'
  // for sales_agent role regardless of any state drift.
  const isAgent = staffRole === 'sales_agent';
  const [tab, setTab] = useState<Tab>('agent');
  const effectiveTab: Tab = isAgent ? 'agent' : tab;
  // AC-3: this page and the dashboard tile must agree for the same period.
  // They now share the filter model AND the predicate -- same inputs, same
  // `inPeriod` over the raw event timestamp -- so they cannot drift. The old
  // server-side `.eq('commission_month', ...)` is gone: it filtered on the
  // DERIVED bucket, which is the one thing the dashboard deliberately avoids
  // (architect ruling AR-1).
  const filters = useFilterParams({ defaultPeriod: 'this-month' });

  const [allAgentRows, setAllAgentRows] = useState<AgentCommissionRow[]>([]);
  const [allCompanyRows, setAllCompanyRows] = useState<CompanyCommissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch once, unfiltered; the period is applied below. At current volume the
  // whole set arrives in one round trip, and re-fetching per chip press would
  // buy nothing (AR-1.1 records the trip-wire for pushing this down).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      salesService.listAgentCommission(),
      salesService.listCompanyCommission(),
    ])
      .then(([a, c]) => {
        if (cancelled) return;
        setAllAgentRows(a);
        setAllCompanyRows(c);
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Filter on the raw event timestamp, never on `commission_month`.
  const agentRows = useMemo(
    () => allAgentRows.filter((r) => inPeriod(r.sale_date, filters.range)),
    [allAgentRows, filters.range],
  );
  const companyRows = useMemo(
    () => allCompanyRows.filter((r) => inPeriod(r.commission_date, filters.range)),
    [allCompanyRows, filters.range],
  );

  const agentTotal = useMemo(() => agentRows.reduce((s, r) => s + Number(r.commission_amount ?? 0), 0), [agentRows]);
  const companyTotal = useMemo(() => companyRows.reduce((s, r) => s + Number(r.commission_amount ?? 0), 0), [companyRows]);

  function downloadCsv() {
    const rows = effectiveTab === 'agent' ? agentRowsToCsv(agentRows) : companyRowsToCsv(companyRows);
    const blob = new Blob([rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Name the file by the period actually exported, so two downloads taken on
    // the same day for different periods do not collide.
    const span = filters.range.start
      ? `${filters.range.start}_to_${filters.range.end ?? 'today'}`
      : 'all-time';
    a.download = `sighthost-${effectiveTab}-commission-${span}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <UniversalPage
      title="Commission"
      description={isAgent
        ? "R50 / R500 once-off · CSV export"
        : "R50 / R500 once-off · company recurring · CSV export for external payouts"}
      actions={
        <Button onClick={downloadCsv} variant="outline" disabled={loading || (effectiveTab === 'agent' ? agentRows.length === 0 : companyRows.length === 0)}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      }
    >
      {/* Filters */}
      <Card className="flex flex-wrap items-center gap-3 p-4">
        <div className="flex gap-2">
          <Button onClick={() => setTab('agent')} variant={effectiveTab === 'agent' ? 'primary' : 'outline'} size="sm">
            Agent ({formatZAR(agentTotal)})
          </Button>
          {!isAgent && (
            <Button onClick={() => setTab('company')} variant={effectiveTab === 'company' ? 'primary' : 'outline'} size="sm">
              Company recurring ({formatZAR(companyTotal)})
            </Button>
          )}
        </div>
        {/* Was a raw <select> with hand-written border/background classes -- a
            §4.2 violation whose focus ring and height matched nothing else on
            the page. `bordered={false}` because this Card already exists. */}
        <div className="ml-auto">
          <UniversalFilterBar
            bordered={false}
            activeCount={filters.activeCount}
            onClear={filters.clear}
            period={
              <PeriodFilter
                value={filters.period}
                from={filters.from}
                to={filters.to}
                onChange={filters.setPeriod}
                label="Commission period"
              />
            }
          />
        </div>
      </Card>

      {error && <Card className="border-danger/30 bg-danger/5 p-4 text-sm text-danger">{error}</Card>}

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : effectiveTab === 'agent' ? (
        agentRows.length === 0 ? (
          <UniversalEmptyState
            title="No agent commission for this period"
            description="Once a converted lead's first invoice is paid, an R50 (hosting/Care/Email) or R500 (Studio) row appears here."
          />
        ) : (
          <UniversalTable<AgentCommissionRow>
            columns={agentColumns}
            data={agentRows}
            getRowKey={(r) => `${r.customer_id}-${r.target_kind}-${r.target_ref}`}
            caption={`${agentRows.length} row(s) · ${formatZAR(agentTotal)} total`}
          />
        )
      ) : companyRows.length === 0 ? (
        <UniversalEmptyState
          title="No company recurring commission for this period"
          description="Your company's contracted recurring percentage of qualifying hosting + Care payments appears here — renewals included. Your rate is shown on Sales → Companies."
        />
      ) : (
        <UniversalTable<CompanyCommissionRow>
          columns={companyColumns}
          data={companyRows}
          getRowKey={(r) => `${r.invoice_id}-${r.target_kind}-${r.target_ref}`}
          caption={`${companyRows.length} row(s) · ${formatZAR(companyTotal)} total`}
        />
      )}
    </UniversalPage>
  );
}

const agentColumns: UniversalTableColumn<AgentCommissionRow>[] = [
  { key: 'sale_date', header: 'Sale date', render: (r) => new Date(r.sale_date).toLocaleDateString('en-ZA') },
  // A partner-link sale has no agent (agent_id NULL since 2026-09-25). The
  // once-off is still the Partner's — agreement 4.3A: all commission is paid to
  // the Partner, who distributes to its agents — so the row shows and is
  // labelled rather than crashing on .slice(). Do not render "—": an unexplained
  // blank on a money page reads as an error.
  { key: 'agent_name', header: 'Agent', render: (r) => r.agent_name ?? r.agent_email ?? (r.agent_id ? r.agent_id.slice(0, 8) : 'Partner link') },
  { key: 'company_name', header: 'Company', render: (r) => r.company_name ?? '—' },
  { key: 'target_kind', header: 'Product', render: (r) => `${r.target_kind} · ${r.target_ref ?? ''}` },
  { key: 'invoice_number', header: 'Invoice', render: (r) => r.invoice_number ?? r.invoice_id.slice(0, 8) },
  { key: 'commission_amount', header: 'Amount', align: 'right', render: (r) => formatZAR(r.commission_amount) },
];

const companyColumns: UniversalTableColumn<CompanyCommissionRow>[] = [
  { key: 'commission_date', header: 'Paid', render: (r) => new Date(r.commission_date).toLocaleDateString('en-ZA') },
  { key: 'company_name', header: 'Company', render: (r) => r.company_name },
  { key: 'target_kind', header: 'Product', render: (r) => `${r.target_kind} · ${r.target_ref ?? ''}` },
  { key: 'line_amount', header: 'Line', align: 'right', render: (r) => formatZAR(r.line_amount) },
  { key: 'recurring_commission_pct', header: '%', align: 'right', render: (r) => `${r.recurring_commission_pct}%` },
  { key: 'commission_amount', header: 'Commission', align: 'right', render: (r) => formatZAR(r.commission_amount) },
];


function agentRowsToCsv(rows: AgentCommissionRow[]): string {
  const header = 'sale_date,agent_name,agent_email,company_name,target_kind,target_ref,invoice_number,commission_amount\n';
  const body = rows.map((r) => [
    r.sale_date,
    csv(r.agent_name ?? ''),
    csv(r.agent_email ?? ''),
    csv(r.company_name ?? ''),
    r.target_kind,
    csv(r.target_ref ?? ''),
    csv(r.invoice_number ?? ''),
    String(r.commission_amount),
  ].join(',')).join('\n');
  return header + body;
}

function companyRowsToCsv(rows: CompanyCommissionRow[]): string {
  const header = 'commission_date,company_name,company_slug,target_kind,target_ref,invoice_number,line_amount,recurring_commission_pct,commission_amount\n';
  const body = rows.map((r) => [
    r.commission_date,
    csv(r.company_name),
    csv(r.company_slug),
    r.target_kind,
    csv(r.target_ref ?? ''),
    csv(r.invoice_number ?? ''),
    String(r.line_amount),
    String(r.recurring_commission_pct),
    String(r.commission_amount),
  ].join(',')).join('\n');
  return header + body;
}

function csv(s: string): string {
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
